import { Pressable, StyleSheet, Text, View } from "react-native";

import { Icon, type IconName } from "../Icon/Icon";
import type { UiTheme } from "../theme";
import { Sheet } from "./Sheet";

// A list of actions in a bottom Sheet: icon + label rows, 44pt each, one
// destructive tone, disabled rows stay visible and say so to a reader.
// Born with the phone program editor's day and exercise menus (#336); the
// Logger's superset menu rides on it too.

export type SheetMenuItem = {
  key: string;
  label: string;
  icon: IconName;
  onPress: () => void;
  disabled?: boolean;
  destructive?: boolean;
  /** Fill the icon with its own tone (the template star). */
  fill?: boolean;
  /** A line under the label: why a disabled item is disabled. Also spoken. */
  hint?: string;
};

export type SheetMenuProps = {
  theme: UiTheme;
  visible: boolean;
  onClose: () => void;
  title: string;
  /** Label of the header ✕. Omit it for a sheet that dismisses by `cancelLabel`. */
  closeLabel?: string;
  /** A full-width Cancel row under the items, instead of the header ✕. */
  cancelLabel?: string;
  items: readonly SheetMenuItem[];
  bottomInset?: number;
};

export function SheetMenu({
  theme,
  visible,
  onClose,
  title,
  closeLabel,
  cancelLabel,
  items,
  bottomInset,
}: SheetMenuProps) {
  const styles = makeStyles(theme);
  const p = theme.palette;
  const list = (
    <View>
      {items.map((item) => {
        const tone = item.disabled ? p.ink3 : item.destructive ? p.danger : p.ink;
        return (
          <Pressable
            key={item.key}
            accessibilityRole="button"
            accessibilityState={{ disabled: item.disabled || undefined }}
            accessibilityHint={item.hint}
            disabled={item.disabled}
            // The sheet closes first: the action may open a dialog of its own.
            onPress={() => {
              onClose();
              item.onPress();
            }}
            style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
          >
            <Icon
              name={item.icon}
              size={theme.typography.sizes.lg}
              color={tone}
              fill={item.fill ? tone : undefined}
            />
            {item.hint ? (
              <View style={styles.labels}>
                <Text style={[styles.label, { color: tone }]}>{item.label}</Text>
                <Text style={styles.hint}>{item.hint}</Text>
              </View>
            ) : (
              <Text style={[styles.label, { color: tone }]}>{item.label}</Text>
            )}
          </Pressable>
        );
      })}
      {cancelLabel === undefined ? null : (
        <Pressable accessibilityRole="button" onPress={onClose} style={styles.cancel}>
          <Text style={styles.cancelText}>{cancelLabel}</Text>
        </Pressable>
      )}
    </View>
  );
  const common = { theme, visible, onRequestClose: onClose, title, bottomInset } as const;
  return closeLabel === undefined ? (
    <Sheet {...common}>{list}</Sheet>
  ) : (
    <Sheet {...common} onClose={onClose} closeAccessibilityLabel={closeLabel}>
      {list}
    </Sheet>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s4,
      minHeight: 44,
      paddingHorizontal: s.s2,
    },
    rowPressed: { backgroundColor: p.surface2, borderRadius: r.r3 },
    cancel: {
      marginTop: s.s2,
      minHeight: 44,
      alignItems: "center",
      justifyContent: "center",
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: p.border,
    },
    cancelText: { color: p.brand500, fontSize: t.sizes.base, fontWeight: "700" },
    label: { fontSize: t.sizes.base, fontWeight: "600", flexShrink: 1 },
    labels: { flexShrink: 1, paddingVertical: s.s2 },
    hint: { color: p.ink2, fontSize: t.sizes.sm },
  });
}
